import ActionFeedBackModal from '@/components/ActionFeedbackModal';
import ConfirmDialog from '@/components/ConfirmDialog';
import Loader from '@/components/Loader';
import PersonImage from '@/components/PersonImage';
import ResizableTextarea from '@/components/ResizableTextarea';
import Constants from '@/Constants';
import { Person, PersonPostPutRequest } from '@/models/Person';
import { analyticsService, FlowEvents } from '@/services/AnalyticsService';
import { uploadFile } from '@/services/S3Service';
import { removeSpacesAndSpecialCharacters } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { getPersonClear, getPersonRequest, putPersonRequest } from '@/store/person/action';
import React, { useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import Modal from 'react-bootstrap/esm/Modal';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'underscore';
import './style.scss';

interface ProfileEditForm {
    thumbnail: string | undefined;
    displayName: string | undefined;
    aboutMe: string | undefined;
    position: boolean | undefined;
    email: boolean | undefined;
    departmentName: boolean | undefined;
}

interface ProfileEditModalProps {
    showProfileEditModal: boolean;
    handleModalClose: () => void;
}
const ProfileEditModal: React.FC<ProfileEditModalProps> = ({ showProfileEditModal, handleModalClose }: ProfileEditModalProps) => {
    const dispatch = useDispatch();

    const { strings, person, personId, loading, successOnPutPerson } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        personId: state.identity.data.person.id,
        person: state.people.person?.person || ({} as Person),
        loading: state.people.loading,
        successOnPutPerson: state.people.successOnPutPerson,
    }));

    const personDataVisibility = person.personDataVisibility || {
        personId,
        departmentName: false,
        email: false,
        division: false,
        regional: false,
        position: false,
        gender: false,
        registration: false,
        admissionDate: false,
    };

    const formInitialState = {
        thumbnail: person.thumbnail,
        displayName: person.displayName,
        aboutMe: person.aboutMe,
        position: personDataVisibility.position,
        email: personDataVisibility.email,
        departmentName: personDataVisibility.departmentName,
    };

    const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true);
    const [showCloseConfirm, setShowCloseConfirm] = useState<boolean>(false);
    const [newThumbnailFile, setNewThumbnailFile] = useState<File>();
    const [uploadingThumbnail, setUploadingThumbnail] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>();
    const [saving, setSaving] = useState<boolean>(false);
    const [ifEmptyDisplayName, setIfEmptyDisplayName] = useState<boolean>(true);
    const [ifEmptyAboutMe, setIfEmptyAboutMe] = useState<boolean>(true);

    const [formState, setFormState] = useState<ProfileEditForm>(formInitialState);
    const { displayName, aboutMe, position, email, departmentName, thumbnail } = formState;

    const handleOnChange = (fieldName: string, value: string | boolean) => {
        setFormState((prevState) => ({ ...prevState, [fieldName]: value }));
    };

    const handleOnPressSave = async () => {
        setSaving(true);

        analyticsService.logEvent(FlowEvents.EDIT_MY_PROFILE);

        let updatedPerson = {
            ...person,
            displayName,
            aboutMe,
            personDataVisibility: {
                ...personDataVisibility,
                position,
                email,
                departmentName,
            },
        };

        if (newThumbnailFile) {
            setUploadingThumbnail(true);

            const key: string = `${new Date().getTime()}-${removeSpacesAndSpecialCharacters(newThumbnailFile.name)}`;

            const url = await uploadFile(newThumbnailFile, key, () => {});

            if (url !== null) {
                updatedPerson = { ...updatedPerson, thumbnail: key };
            }

            setUploadingThumbnail(false);
        }

        dispatch(putPersonRequest({ person: updatedPerson, willClose: false } as PersonPostPutRequest));
    };

    const confirmCloseModal = (close: boolean) => {
        setShowCloseConfirm(false);

        if (close) {
            handleModalClose();
            setNewThumbnailFile(undefined);
            setSuccess(undefined);
            setSaving(false);
            dispatch(getPersonClear());
        }
    };

    const closeEditModal = () => {
        if (!(uploadingThumbnail || loading)) {
            isSaveDisabled ? confirmCloseModal(true) : setShowCloseConfirm(true);
        }
    };

    const acceptFileThumbs = 'image/png,image/jpeg';

    const handleSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            if (e.target.files.length > 1) {
                return;
            }

            const file = e.target.files[0];

            if (acceptFileThumbs.split(',').find((type: string) => type === file.type) === undefined) {
                return;
            }

            const img = URL.createObjectURL(file);

            setNewThumbnailFile(file);
            handleOnChange('thumbnail', img);
        }
    };

    const getVisibilityDropDown = (fieldName: string, value: boolean | undefined) => {
        return (
            <Dropdown>
                <Dropdown.Toggle>
                    <i className="material-icons">{value ? Constants.icons.PUBLIC : Constants.icons.LOCK}</i>
                </Dropdown.Toggle>

                <Dropdown.Menu alignRight id="bg-nested-dropdown" title="">
                    <Dropdown.Item
                        eventKey={_.uniqueId('dropDownItem_')}
                        href="#"
                        key={_.uniqueId('dropDownItem_')}
                        onClick={() => handleOnChange(fieldName, true)}
                        target="_blank"
                    >
                        <i className="material-icons-outlined">{Constants.icons.PUBLIC}</i>
                        <span>{strings.PUBLIC}</span>
                    </Dropdown.Item>
                    <Dropdown.Item
                        eventKey={_.uniqueId('dropDownItem_')}
                        href="#"
                        key={_.uniqueId('dropDownItem_')}
                        onClick={() => handleOnChange(fieldName, false)}
                        target="_blank"
                    >
                        <i className="material-icons">{Constants.icons.LOCK}</i>
                        <span>{strings.PRIVATE}</span>
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>
        );
    };

    useEffect(() => {
        if (showProfileEditModal) {
            dispatch(getPersonRequest(personId as number, true));
        }
    }, [showProfileEditModal]);

    useEffect(() => {
        if (person.id) {
            setFormState(formInitialState);
        }
    }, [person]);

    useEffect(() => {
        setIsSaveDisabled(_.isMatch(formState, formInitialState));
    }, [formState]);

    useEffect(() => {
        if (successOnPutPerson) {
            confirmCloseModal(true);
        }

        if (successOnPutPerson !== undefined) {
            setSuccess(successOnPutPerson);
        }
    }, [successOnPutPerson]);

    useEffect(() => {
        return () => {
            dispatch(getPersonClear());
        };
    }, []);

    useEffect(() => {
        setIfEmptyDisplayName(validateEmptyTextInput(String(displayName)));
        setIfEmptyAboutMe(validateEmptyTextInput(String(aboutMe)));
    }, [displayName, aboutMe]);

    const validateEmptyTextInput = (textState: string): boolean => {
        const isCleanTextInput: boolean = !!textState.trim();
        if (isCleanTextInput || textState.length === 0) return false;
        if (!isCleanTextInput && textState.length > 0) return true;
        return true;
    };

    return (
        <>
            <Modal centered id="profile-edit-modal" show={showProfileEditModal && !showCloseConfirm}>
                <div className="profile-edit-modal">
                    {(loading || uploadingThumbnail) && <Loader />}
                    <div className="change-modal-content modal-content ">
                        {person.id && (
                            <>
                                <div className="modal-title title-profile-edit">
                                    <div className="text-title">
                                        <h1> {strings.EDIT_PROFILE.EDIT_PROFILE}</h1>
                                    </div>
                                    <i
                                        className="material-icons-outlined align-middle close-icon"
                                        data-dismiss="modal"
                                        onClick={() => {
                                            closeEditModal();
                                        }}
                                        onKeyDown={() => {}}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        {Constants.icons.CLOSE}
                                    </i>
                                </div>
                                <div className="input-section">
                                    <label className="user-image" htmlFor="file">
                                        <div className="update-thumbnail">
                                            <span className="material-icons">{Constants.icons.CAMERA_ALT}</span>
                                            <span>{strings.UPDATE_PHOTO}</span>
                                        </div>
                                        <PersonImage size={82} src={thumbnail} />
                                    </label>
                                    <input accept={acceptFileThumbs} id="file" onChange={handleSelectFile} style={{ display: 'none' }} type="file" />
                                </div>
                                <div className="input-section">
                                    <div className="title-container">
                                        <h1>{strings.FULL_NAME}</h1>
                                    </div>
                                    <input className="form-control" disabled type="text" value={`${person.firstName} ${person.lastName}`} />
                                </div>
                                <div className="input-section">
                                    <div className="title-container">
                                        <h1>{strings.DISPLAY_NAME}</h1>
                                    </div>
                                    <input
                                        className="form-control"
                                        maxLength={120}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOnChange('displayName', e.target.value)}
                                        placeholder={`${person.firstName} ${person.lastName}`}
                                        type="text"
                                        value={displayName}
                                    />
                                    <p>{strings.EDIT_PROFILE.DISPLAY_NAME_INFO}</p>
                                </div>
                                <div className="input-section">
                                    <div className="title-container">
                                        <h1>{strings.ABOUT_ME}</h1>
                                    </div>
                                    <ResizableTextarea
                                        countChars={true}
                                        maxLength={120}
                                        setValue={(value: string) => handleOnChange('aboutMe', value)}
                                        textAreaClassName="form-control"
                                        value={aboutMe || ''}
                                    />
                                </div>
                                {Boolean(person.position?.trim()) && (
                                    <div className="input-section">
                                        <div className="title-container">
                                            <h1>{strings.POSITION}</h1>
                                            {getVisibilityDropDown('position', position)}
                                        </div>
                                        <input className="form-control" disabled type="text" value={person.position} />
                                    </div>
                                )}
                                <div className="input-section">
                                    <div className="title-container">
                                        <h1>{strings.EMAIL}</h1>
                                        {getVisibilityDropDown('email', email)}
                                    </div>
                                    <input className="form-control" disabled type="text" value={person.email} />
                                </div>
                                {Boolean(person.departmentName?.trim()) && (
                                    <div className="input-section">
                                        <div className="title-container">
                                            <h1>{strings.DEPARTMENT}</h1>
                                            {getVisibilityDropDown('departmentName', departmentName)}
                                        </div>
                                        <input className="form-control" disabled type="text" value={person.departmentName} />
                                    </div>
                                )}
                                <div className="input-section">
                                    <div className="title-container">
                                        <p>
                                            <i className="material-icons-outlined">{Constants.icons.LIGHTBULB}</i>
                                            {strings.EDIT_PROFILE.COMPANY_CHANGE_INFO}
                                        </p>
                                    </div>
                                </div>
                                <div className="modal-confirm-button">
                                    <button
                                        className="btn btn-secondary confirm-button"
                                        disabled={isSaveDisabled || loading || uploadingThumbnail || ifEmptyDisplayName || ifEmptyAboutMe}
                                        onClick={handleOnPressSave}
                                        type="button"
                                    >
                                        {strings.SAVE}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </Modal>
            <ConfirmDialog
                content={strings.LEAVE_SECTION_MESSAGE}
                leftButtonTitle={strings.CONFIRM_EXIT}
                leftClick={() => confirmCloseModal(true)}
                rightClick={() => confirmCloseModal(false)}
                show={showCloseConfirm}
            />
            {saving && (
                <ActionFeedBackModal
                    error={success === false}
                    errorMessage={strings.ERROR_WHILE_SAVE_DATA}
                    loading={loading}
                    onHide={() => setSuccess(undefined)}
                    success={success === true}
                    successMessage={strings.SUCCESS_ON_SAVE}
                />
            )}
        </>
    );
};

export default ProfileEditModal;
